import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { openChatSocket } from "@/lib/ws";
import { ResendBulkPasteModal } from "@/components/ResendBulkPaste";
import { EkycChatButton } from "@/components/EkycChatButton";
import {
  LogOut,
  Send,
  CheckCircle2,
  Clock,
  ClipboardPaste,
  X,
  AlertTriangle,
  Inbox,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

// Tap-to-copy — strip non-digits, cap at 17 chars (LPG-ID max length), and
// flash a toast confirmation. Used for LPG-ID and DAC code cells so staff
// can one-tap paste into the agency portal.
function copyDigits(raw) {
  const digits = String(raw || "").replace(/\D/g, "").slice(0, 17);
  if (!digits) return null;
  try {
    navigator.clipboard.writeText(digits);
    toast.success(`Copied · ${digits}`, { duration: 1200 });
    return digits;
  } catch (_e) {
    return null; /* clipboard denied — silently ignore */
  }
}

/**
 * CopyChip — an inline button that behaves exactly like real-time
 * "copy on tap" UX: shows the LPG ID normally, briefly flips to a
 * green "✓ Copied" pill for ~1.1s on click, then reverts. This makes
 * the copy action feel immediate/tactile even without focusing on the
 * toast.
 */
function CopyChip({ value, testId, className = "" }) {
  const [flash, setFlash] = React.useState(false);
  const handle = (ev) => {
    ev.stopPropagation();
    const ok = copyDigits(value);
    if (!ok) return;
    setFlash(true);
    window.setTimeout(() => setFlash(false), 1100);
  };
  return (
    <button
      type="button"
      onClick={handle}
      data-testid={testId}
      title="Tap to copy — sirf digits, max 17"
      className={`inline-flex items-center gap-1 font-bold cursor-pointer bg-transparent border-0 p-0 font-mono transition-colors duration-150 ${
        flash
          ? "text-emerald-700"
          : "text-[#1C1C19] hover:text-emerald-700 hover:underline"
      } ${className}`}
    >
      {flash ? (
        <span
          className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[10px] tracking-wider font-mono animate-pulse"
          data-testid={`${testId}-copied-flash`}
        >
          ✓ COPIED
        </span>
      ) : (
        value
      )}
    </button>
  );
}

/**
 * Locked-down landing page for `resend_staff` users.
 *
 * What they see:
 *   • Their identity + team badge (top-right).
 *   • Inline "File a resend" form — agency typeahead (fuzzy + first-word
 *     match with mismatch warning), LPG ID, optional DAC / Mobile / Reason.
 *   • Their OWN pending + recently-confirmed entries (server filters by
 *     `created_by_user_id` automatically for the resend_staff role).
 *
 * What they don't see: chat, admin, bulk-paste, reports, settings — all
 * routes are blocked by `ProtectedRoute` (URL-direct attempts redirect
 * back here) AND by the backend middleware (`restrict_resend_staff`) so
 * even raw API calls 403.
 */
export default function ResendOnly() {
  const { user, logout } = useAuth();
  const [agencyName, setAgencyName] = useState("");
  const [lpgId, setLpgId] = useState("");
  const [dac, setDac] = useState("");
  const [mobile, setMobile] = useState("");
  const [reason, setReason] = useState("");
  const [match, setMatch] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [needsConfirm, setNeedsConfirm] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [pending, setPending] = useState([]);
  const [confirmed, setConfirmed] = useState([]);
  const [received, setReceived] = useState([]);
  const [escalated, setEscalated] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [agencies, setAgencies] = useState([]);
  const [bulkSelected, setBulkSelected] = useState(new Set());
  // Bulk paste sub-mode — toggle next to "New request" header.
  const [bulkOpen, setBulkOpen] = useState(false);

  // Fetch agency list for the dropdown. Runs on mount + on window/tab focus
  // + every 60s + when the agency input is focused. This way new groups
  // created by admin (or renamed groups) appear here without a page reload.
  const fetchAgencies = React.useCallback(async () => {
    try {
      const { data } = await api.get("/resend/agencies");
      setAgencies(data.agencies || []);
    } catch (_e) {
      // Leave prior agency list intact on transient error — logging
      // helps diagnose if the polling loop starts silently failing.
      console.error("fetchAgencies failed:", _e);
    }
  }, []);

  useEffect(() => {
    fetchAgencies();
    const onFocus = () => fetchAgencies();
    window.addEventListener("focus", onFocus);
    const iv = setInterval(fetchAgencies, 60_000);
    return () => {
      window.removeEventListener("focus", onFocus);
      clearInterval(iv);
    };
  }, [fetchAgencies]);

  // Debounced agency resolve.
  useEffect(() => {
    setMatch(null);
    setErr("");
    setNeedsConfirm(null);
    const name = agencyName.trim();
    if (name.length < 2) return;
    const t = setTimeout(async () => {
      setResolving(true);
      try {
        const { data } = await api.post("/resend/resolve", {
          agency_name: name,
        });
        setMatch(data);
      } catch (_e) {
        setMatch(null);
      } finally {
        setResolving(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [agencyName]);

  const loadMine = async () => {
    setLoadingList(true);
    try {
      const [p, c, r, e] = await Promise.all([
        api.get("/resend/entries?status=pending&days=14"),
        api.get("/resend/entries?status=confirmed&days=14"),
        api.get("/resend/entries?status=received&days=14"),
        api.get("/resend/entries?status=escalated&days=30"),
      ]);
      setPending(p.data.entries || []);
      setConfirmed(c.data.entries || []);
      setReceived(r.data.entries || []);
      setEscalated(e.data.entries || []);
    } catch (_e) {
      // Surface as inline error rather than blocking the form.
      console.error("resend-list fetch failed:", _e);
    } finally {
      setLoadingList(false);
    }
  };

  const markReceived = async (id) => {
    try {
      await api.post(`/resend/entries/${id}/receive`, {});
      loadMine();
    } catch (e) {
      setErr(formatApiError(e));
    }
  };
  const toggleBulk = (id) => {
    setBulkSelected((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return s;
    });
  };
  const bulkMarkReceived = async () => {
    const ids = Array.from(bulkSelected);
    if (!ids.length) return;
    try {
      await api.post("/resend/entries/bulk/receive", { ids });
      setBulkSelected(new Set());
      loadMine();
    } catch (e) {
      setErr(formatApiError(e));
    }
  };
  const clearReceived = async () => {
    if (!window.confirm(`Purani ${received.length} received entries permanently delete karni hain?`)) return;
    try {
      await api.post("/resend/entries/clear?scope=mine");
      loadMine();
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  useEffect(() => {
    loadMine();
  }, []);

  // Real-time sync via WebSocket. Backend broadcasts `resend.new`,
  // `resend.confirmed`, `resend.received`, `resend.retried` to every
  // staff/member. Any such event triggers a debounced refetch so the
  // 4-column board stays live without a manual refresh.
  const loadMineRef = useRef(loadMine);
  useEffect(() => {
    loadMineRef.current = loadMine;
  });
  useEffect(() => {
    const token = localStorage.getItem("gc_token");
    if (!token) return;
    let scheduled = null;
    const scheduleReload = () => {
      if (scheduled) return;
      scheduled = window.setTimeout(() => {
        scheduled = null;
        if (loadMineRef.current) loadMineRef.current();
      }, 250); // debounce so a burst of events triggers ONE fetch
    };
    const ws = openChatSocket(
      token,
      (data) => {
        if (
          data &&
          typeof data.type === "string" &&
          data.type.startsWith("resend.")
        ) {
          scheduleReload();
        } else if (data && data.type === "groups.renamed") {
          // Admin renamed an agency group — refresh dropdown immediately.
          fetchAgencies();
        }
      },
      scheduleReload, // also refetch on reconnect
    );
    return () => {
      if (scheduled) window.clearTimeout(scheduled);
      try {
        ws.close();
      } catch (_e) {
        // WebSocket close on already-closed socket — safe to ignore
        // (browsers may throw InvalidStateError). Kept as debug log
        // to help diagnose reconnect storms.
        console.debug("ws.close on cleanup threw (safe):", _e);
      }
    };
  }, []);

  const canSubmit =
    agencyName.trim().length >= 2 &&
    /^\d{4,20}$/.test(lpgId.trim()) &&
    !submitting;

  const doSubmit = async (force = false) => {
    setErr("");
    setOk("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/resend/entries", {
        agency_name: agencyName.trim(),
        lpg_id: lpgId.trim(),
        dac: dac.trim(),
        mobile: mobile.trim(),
        reason: reason.trim(),
        force,
      });
      if (data.needs_confirm) {
        setNeedsConfirm({
          warning: data.warning,
          suggested: data.suggested_group_name,
        });
        return;
      }
      setOk(`✓ Sent to ${data.entry.agency_resolved}`);
      setLpgId("");
      setDac("");
      setMobile("");
      setReason("");
      setNeedsConfirm(null);
      loadMine();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F0]" data-testid="resend-only-page">
      <header className="bg-[#1C1C19] text-[#F5F5F0] px-6 py-4 flex items-center justify-between">
        <div>
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-amber-300">
            Resend Desk
          </div>
          <div className="text-lg font-semibold">File a DAC resend request</div>
        </div>
        <div className="flex items-center gap-4">
          {(user?.linked_resend_teams || []).length > 0 && (
            <a
              href="/my-ledger"
              data-testid="resend-only-goto-my-ledger"
              className="px-3 py-2 text-xs font-mono tracking-[0.2em] uppercase border border-amber-300/50 text-amber-300 hover:bg-amber-300/10 rounded"
            >
              ← My Ledger
            </a>
          )}
          <div className="text-right" data-testid="resend-only-identity">
            <div className="text-sm font-semibold">{user?.display_name}</div>
            {user?.resend_team && (
              <div
                className="text-[10px] font-mono tracking-[0.2em] uppercase text-amber-300"
                data-testid="resend-only-team-badge"
              >
                {user.resend_team}
              </div>
            )}
            {(user?.linked_resend_teams || []).length > 0 && (
              <div
                className="text-[10px] font-mono tracking-[0.2em] uppercase text-amber-300"
                data-testid="resend-only-linked-teams-badge"
              >
                Team lead · {(user.linked_resend_teams || []).join(", ")}
              </div>
            )}
          </div>
          <EkycChatButton className="ml-auto mr-2 border border-[#F5F5F0]/30 hover:bg-[#F5F5F0]/10" />
          <button
            onClick={logout}
            data-testid="resend-only-logout"
            className="px-3 py-2 text-xs font-mono tracking-[0.2em] uppercase border border-[#F5F5F0]/30 hover:bg-[#F5F5F0]/10 rounded flex items-center gap-2"
          >
            <LogOut className="w-3.5 h-3.5" />
            Logout
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-6 space-y-6">
        {/* Form card */}
        <section className="bg-white border border-[#DCDCD3] rounded-lg p-5 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-[10px] font-mono tracking-[0.25em] uppercase text-[#8A6046]">
              New request
            </div>
            <button
              type="button"
              onClick={() => setBulkOpen(true)}
              data-testid="resend-only-open-bulk"
              className="text-[10px] font-mono tracking-[0.2em] uppercase px-3 py-1.5 border border-amber-500 text-amber-700 hover:bg-amber-50 rounded flex items-center gap-1.5"
            >
              <ClipboardPaste className="w-3.5 h-3.5" />
              Bulk paste
            </button>
          </div>

          <div>
            <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#6B6B61] block mb-1">
              Agency name *
            </label>
            <input
              value={agencyName}
              onChange={(e) => setAgencyName(e.target.value)}
              onFocus={fetchAgencies}
              placeholder="Type or select from list"
              list="resend-agency-options"
              data-testid="resend-only-agency"
              className="w-full px-3 py-2 border border-[#DCDCD3] rounded text-sm focus:outline-none focus:border-[#385C4B] bg-white"
            />
            <datalist id="resend-agency-options">
              {agencies.map((a) => (
                <option key={a.id} value={a.name} />
              ))}
            </datalist>
            <p className="text-[10px] text-[#6B6B61] mt-1 font-mono">
              {agencies.length} Bharat Gas / BPCL agencies · type to filter or pick from dropdown
            </p>
            {resolving && (
              <p className="text-[10px] text-[#6B6B61] mt-1 font-mono">checking…</p>
            )}
            {!resolving && match && match.group_id && (
              <p
                className={`text-[11px] mt-1 font-mono ${
                  match.exact ? "text-emerald-700" : "text-amber-700"
                }`}
                data-testid="resend-only-match"
              >
                {match.exact ? "✓ matches" : "⚠ closest match"}: {match.group_name}
                {!match.exact && match.warning ? ` — ${match.warning}` : ""}
              </p>
            )}
            {!resolving &&
              match &&
              !match.group_id &&
              agencyName.trim().length >= 2 && (
                <p className="text-[11px] text-red-700 mt-1 font-mono">
                  ✗ no agency matched — check spelling
                </p>
              )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#6B6B61] block mb-1">
                LPG ID *
              </label>
              <input
                value={lpgId}
                onChange={(e) =>
                  setLpgId(e.target.value.replace(/\D/g, "").slice(0, 20))
                }
                placeholder="6–17 digits"
                inputMode="numeric"
                data-testid="resend-only-lpg"
                className="w-full px-3 py-2 border border-[#DCDCD3] rounded text-sm focus:outline-none focus:border-[#385C4B] bg-white font-mono"
              />
            </div>
            <div>
              <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#6B6B61] block mb-1">
                DAC (optional)
              </label>
              <input
                value={dac}
                onChange={(e) =>
                  setDac(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                placeholder="4-digit code"
                inputMode="numeric"
                data-testid="resend-only-dac"
                className="w-full px-3 py-2 border border-[#DCDCD3] rounded text-sm focus:outline-none focus:border-[#385C4B] bg-white font-mono"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#6B6B61] block mb-1">
                Mobile (optional)
              </label>
              <input
                value={mobile}
                onChange={(e) =>
                  setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))
                }
                placeholder="10 digits"
                inputMode="numeric"
                data-testid="resend-only-mobile"
                className="w-full px-3 py-2 border border-[#DCDCD3] rounded text-sm focus:outline-none focus:border-[#385C4B] bg-white font-mono"
              />
            </div>
            <div>
              <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#6B6B61] block mb-1">
                Reason (optional)
              </label>
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value.slice(0, 80))}
                placeholder="e.g. DAC not received"
                data-testid="resend-only-reason"
                className="w-full px-3 py-2 border border-[#DCDCD3] rounded text-sm focus:outline-none focus:border-[#385C4B] bg-white"
              />
            </div>
          </div>

          {needsConfirm && (
            <div
              className="rounded border border-amber-400 bg-amber-50 p-3 text-sm text-amber-900"
              data-testid="resend-only-needs-confirm"
            >
              <div className="font-semibold mb-1">Spelling mismatch warning</div>
              <div className="mb-2">{needsConfirm.warning}</div>
              <button
                onClick={() => doSubmit(true)}
                disabled={submitting}
                data-testid="resend-only-force-submit"
                className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded text-xs font-semibold"
              >
                {submitting ? "Submitting…" : `Yes, send to ${needsConfirm.suggested}`}
              </button>
            </div>
          )}

          {err && (
            <div
              className="rounded border border-red-300 bg-red-50 p-2 text-sm text-red-800"
              data-testid="resend-only-error"
            >
              {err}
            </div>
          )}
          {ok && (
            <div
              className="rounded border border-emerald-300 bg-emerald-50 p-2 text-sm text-emerald-800"
              data-testid="resend-only-success"
            >
              {ok}
            </div>
          )}

          <div className="flex justify-end pt-2 border-t border-[#DCDCD3]">
            <button
              onClick={() => doSubmit(false)}
              disabled={!canSubmit}
              data-testid="resend-only-submit"
              className="px-5 py-2 text-sm bg-[#385C4B] text-white rounded hover:bg-[#2d4a3c] disabled:opacity-50 disabled:cursor-not-allowed font-semibold flex items-center gap-2"
            >
              <Send className="w-4 h-4" />
              {submitting ? "Submitting…" : "Submit Resend"}
            </button>
          </div>
        </section>

        {/* Bulk actions strip — appears only when Confirmed items are picked */}
        {bulkSelected.size > 0 && (
          <div
            className="bg-emerald-50 border border-emerald-300 rounded-lg px-4 py-2.5 flex items-center gap-3 sticky top-0 z-10"
            data-testid="resend-only-bulk-strip"
          >
            <CheckCircle2 className="w-4 h-4 text-emerald-700" />
            <div className="text-sm font-semibold text-emerald-900">
              {bulkSelected.size} selected
            </div>
            <button
              onClick={bulkMarkReceived}
              data-testid="resend-only-bulk-received-btn"
              className="ml-auto px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs font-semibold flex items-center gap-1.5"
            >
              <Inbox className="w-3.5 h-3.5" />
              Received DAC · Mark all
            </button>
            <button
              onClick={() => setBulkSelected(new Set())}
              className="text-xs text-emerald-800 hover:text-emerald-900 underline"
            >
              clear selection
            </button>
          </div>
        )}

        {/* My filed entries — 4 columns: Pending → Confirmed → Received → Escalated */}
        <section className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div
            className="bg-white border border-[#DCDCD3] rounded-lg p-4"
            data-testid="resend-only-pending-card"
          >
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-4 h-4 text-amber-600" />
              <div className="text-[10px] font-mono tracking-[0.25em] uppercase text-[#8A6046]">
                Pending ({pending.length})
              </div>
            </div>
            {loadingList ? (
              <div className="text-xs font-mono text-[#6B6B61]">loading…</div>
            ) : pending.length === 0 ? (
              <div className="text-xs font-mono text-[#6B6B61]">none yet</div>
            ) : (
              <div className="space-y-1.5">
                {pending.slice(0, 30).map((e) => (
                  <ResendRowLine key={e.id} e={e} kind="pending" />
                ))}
              </div>
            )}
          </div>

          <div
            className="bg-white border border-[#DCDCD3] rounded-lg p-4"
            data-testid="resend-only-confirmed-card"
          >
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="w-4 h-4 text-blue-600" />
              <div className="text-[10px] font-mono tracking-[0.25em] uppercase text-blue-700">
                Confirmed ({confirmed.length})
              </div>
            </div>
            {loadingList ? (
              <div className="text-xs font-mono text-[#6B6B61]">loading…</div>
            ) : confirmed.length === 0 ? (
              <div className="text-xs font-mono text-[#6B6B61]">none yet</div>
            ) : (
              <div className="space-y-1.5">
                {confirmed.slice(0, 30).map((e) => (
                  <ResendRowLine
                    key={e.id}
                    e={e}
                    kind="confirmed"
                    onReceive={() => markReceived(e.id)}
                    selected={bulkSelected.has(e.id)}
                    onToggle={() => toggleBulk(e.id)}
                  />
                ))}
              </div>
            )}
          </div>

          <div
            className="bg-white border border-[#DCDCD3] rounded-lg p-4"
            data-testid="resend-only-received-card"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Inbox className="w-4 h-4 text-emerald-600" />
                <div className="text-[10px] font-mono tracking-[0.25em] uppercase text-emerald-700">
                  Received ({received.length})
                </div>
              </div>
              {received.length > 0 && (
                <button
                  onClick={clearReceived}
                  data-testid="resend-only-clear-received"
                  className="text-[10px] text-red-600 hover:text-red-800 font-mono tracking-wider flex items-center gap-1"
                >
                  <Trash2 className="w-3 h-3" /> CLEAR
                </button>
              )}
            </div>
            {loadingList ? (
              <div className="text-xs font-mono text-[#6B6B61]">loading…</div>
            ) : received.length === 0 ? (
              <div className="text-xs font-mono text-[#6B6B61]">none yet</div>
            ) : (
              <div className="space-y-1.5">
                {received.slice(0, 30).map((e) => (
                  <ResendRowLine key={e.id} e={e} kind="received" />
                ))}
              </div>
            )}
          </div>

          <div
            className="bg-white border border-red-300 rounded-lg p-4"
            data-testid="resend-only-escalated-card"
          >
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-4 h-4 text-red-600" />
              <div className="text-[10px] font-mono tracking-[0.25em] uppercase text-red-700">
                Escalated ({escalated.length})
              </div>
            </div>
            {loadingList ? (
              <div className="text-xs font-mono text-[#6B6B61]">loading…</div>
            ) : escalated.length === 0 ? (
              <div className="text-xs font-mono text-[#6B6B61]">none</div>
            ) : (
              <div className="space-y-1.5">
                {escalated.slice(0, 30).map((e) => (
                  <ResendRowLine key={e.id} e={e} kind="escalated" />
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      {bulkOpen && (
        <ResendBulkPasteModal
          onClose={() => setBulkOpen(false)}
          onCommitted={() => {
            setBulkOpen(false);
            loadMine();
          }}
        />
      )}
    </div>
  );
}

function ResendRowLine({ e, kind, onReceive, selected, onToggle }) {
  const ts = (
    e.received_at_display ||
    e.confirmed_at_display ||
    e.created_at ||
    ""
  )
    .slice(0, 16)
    .replace("T", " ");
  const attempts = e.attempts || 1;
  const maxA = e.max_attempts || 5;
  const showAttempts = attempts > 1 || kind === "escalated";
  const palette = {
    pending: "bg-amber-50 border-amber-200",
    confirmed: "bg-blue-50 border-blue-200",
    received: "bg-emerald-50 border-emerald-200",
    escalated: "bg-red-50 border-red-300",
  }[kind] || "bg-white border-[#DCDCD3]";
  return (
    <div
      data-testid={`resend-only-entry-${kind}`}
      className={`text-[11px] font-mono px-2 py-1.5 rounded border ${palette}`}
    >
      <div className="flex items-center gap-2">
        {kind === "confirmed" && (
          <input
            type="checkbox"
            checked={selected || false}
            onChange={onToggle}
            data-testid={`resend-only-bulk-check-${e.id}`}
            className="w-3.5 h-3.5 accent-emerald-600 cursor-pointer flex-shrink-0"
            title="Select for bulk receive"
          />
        )}
        <CopyChip
          value={e.lpg_id}
          testId={`resend-only-copy-lpg-${e.id}`}
        />
        {showAttempts && (
          <span
            className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
              kind === "escalated"
                ? "bg-red-600 text-white"
                : attempts >= maxA - 1
                ? "bg-amber-500 text-white"
                : "bg-[#8A6046] text-white"
            }`}
            title={`Attempt ${attempts} of ${maxA}`}
          >
            {attempts}/{maxA}
          </span>
        )}
        <span className="ml-auto text-[10px] text-[#6B6B61] whitespace-nowrap">
          {ts}
        </span>
      </div>
      {/* Agency name gets its own row so narrow 4-column layouts never
          truncate it to zero width. Falls back to `agency_typed` if the
          resolver never matched, so the row is never blank. */}
      {(e.agency_resolved || e.agency_typed) && (
        <div
          className="mt-1 text-[10px] font-sans font-semibold text-[#385C4B] truncate"
          title={e.agency_resolved || e.agency_typed}
          data-testid={`resend-only-agency-label-${e.id}`}
        >
          {e.agency_resolved || e.agency_typed}
        </div>
      )}
      {/* Dual-date strip — "AGY" is the date agency logged the booking
          (from the pasted Excel), "US" is when our team filed the resend
          ticket. Only rendered when at least one date is meaningful; the
          creation timestamp on the top row already covers `US` for empty
          agency-date rows, so the strip stays out of the way then. */}
      {e.agency_date && (
        <div
          className="mt-1 flex items-center gap-2 text-[9px] font-mono text-[#6B6B61]"
          data-testid={`resend-only-dates-${e.id}`}
        >
          <span
            className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200"
            title="Agency-side date from paste"
          >
            AGY · {e.agency_date}
          </span>
          <span
            className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200"
            title="Our-side filing date (created_at)"
          >
            US · {ts.split(" ")[0] || ts}
          </span>
        </div>
      )}
      {/* Dual tick status + single-tick button — visible on every confirmed
          card so staff can either bulk-tick above or one-tap tick here. */}
      {(e.agency_ticked !== undefined || e.staff_ticked !== undefined) && (
        <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[9px]">
          <span
            className={`px-1.5 py-0.5 rounded ${e.agency_ticked ? "bg-emerald-100 text-emerald-800" : "bg-gray-100 text-gray-500"}`}
          >
            {e.agency_ticked ? "✓" : "☐"} Agency
          </span>
          <span
            className={`px-1.5 py-0.5 rounded ${e.staff_ticked ? "bg-emerald-100 text-emerald-800" : "bg-gray-100 text-gray-500"}`}
          >
            {e.staff_ticked ? "✓" : "☐"} Staff
          </span>
          {kind === "confirmed" && onReceive && (
            <button
              onClick={onReceive}
              data-testid={`resend-only-received-btn-${e.id}`}
              className="ml-auto text-[10px] px-2 py-0.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded font-semibold flex items-center gap-1"
              title="Mark this DAC as received"
            >
              <Inbox className="w-3 h-3" />
              Received
            </button>
          )}
        </div>
      )}
    </div>
  );
}
